import { PropertyMetadata } from "@tylertech/tyler-api-types";
import { LazyLoadedProxyHandler } from "./LazyLoadedProxyHandler";
import { MetadataFormService, MetadataService } from "../services";
import { Utilities } from "@tylertech/shared-module";
export class EntityWrapper
{
    target: any;

    $modCount: number;

    childProperties: any = {};

    properties: PropertyMetadata[] = [];

    wasCreated: boolean = false;

    checkpoint: Record<string, Record<PropertyKey, any>> = {};

    constructor(target: any, private $metadataService: MetadataService)
    {
        if (target["$isEntityWrapper"]) console.error("Cannot wrap a proxy in a proxy.");
        this.target = target;
        this.$modCount = 0;
    }

    SetLazyLoadParent(property: PropertyMetadata, child: EntityWrapper): void
    {
        if (Utilities.isPrototypePollutingKey(property.Name)) return;
        this.target[property.Name] = child;
        if (property.FromColumnId)
        {
            property.FromColumnId.split(",")
                .forEach(s => (this.childProperties[s] = true));
            this.properties.push(property);
        }
    }

    replaceTarget(newTarget: any, emptyObject: boolean): void
    {
        if (newTarget["$isEntityWrapper"]) newTarget = newTarget["$value"];
        if (this.target === newTarget) return;
        this.replaceProperties(this.target, newTarget);
        if (Utilities.IsNullOrUndefined(newTarget['$$TypeName']) && !Utilities.IsNullOrUndefined(this.target['$$TypeName']))
        {
            newTarget['$$TypeName'] = this.target['$$TypeName'];
        }
        this.target = newTarget;
        this.wasCreated = emptyObject;
        this.$modCount = 0;
        if (this.target)
        {
            //if there are any lazy children for our new target reload them
            Object.keys(this.target)
                .forEach((x, _y) =>
                {
                    this.maybeReload(x);
                });
        }
    }

    replaceProperties(target: any, newTarget: any): void
    {
        if (!target || !newTarget) return;
        // if are target has any proxies keep the proxy and replace its target
        Object.keys(target)
            .forEach((x, _y) =>
            {
                if (x.startsWith("$"))
                {
                    return;
                }
                if (Utilities.isPrototypePollutingKey(x))
                {
                    return;
                }
                if (!Utilities.IsNullOrUndefined(target[x]))
                {
                    if (target[x] && target[x].$isEntityWrapper)
                    {
                        const newSub = newTarget[x];
                        newTarget[x] = target[x];
                        if (newSub)
                            if (newSub.$isEntityWrapper)
                                newTarget[x].replaceTarget(newSub["$value"], Utilities.IsNullOrUndefined(newSub));
                            else newTarget[x].replaceTarget(newSub, Utilities.IsNullOrUndefined(newSub));
                    }
                    else if (Array.isArray(target[x]))
                    {
                        const array = target[x];
                        array.length = 0;
                        if (newTarget[x] !== undefined)
                        {
                            array.push(...newTarget[x]);
                        }
                        newTarget[x] = target[x];
                    }
                    else if (typeof target[x] === "object" && target[x] !== null && !(target[x] instanceof Date))
                    {
                        this.replaceProperties(target[x], newTarget[x]);
                    }
                }
            });
    }

    maybeReload(property: string): void
    {
        if (this.childProperties[property])
        {
            for (let index = 0; index < this.properties.length; index++)
            {
                const element = this.properties[index];
                if (element.FromColumnId.split(",")
                    .includes(property))
                {
                    this.reload(element, property);
                }
            }
        }
    }

    reload(propertyToReload: PropertyMetadata, propertyLoadingFrom: string): void
    {
        const currentValue = this.target[propertyLoadingFrom];
        const filter = this.$metadataService.CreateLoadFilter(
            this.target,
            propertyToReload.ToColumnId,
            propertyToReload.FromColumnId,
            propertyToReload.ConstantFilter
        );
        if (!filter)
        {
            this.target[propertyToReload.Name].replaceTarget({}, true);
            return;
        }
        this.$metadataService.Fetch(propertyToReload, filter)
            .then(x =>
            {
                if (this.target[propertyLoadingFrom] === currentValue)
                {
                    const old = this.target[propertyToReload.Name];
                    if (old) old.replaceTarget(x, false);
                    else
                    {
                        if (x["$isEntityWrapper"])
                        {
                            this.target[propertyToReload.Name] = x;
                        }
                        else
                        {
                            const wrapper = new EntityWrapper(x, this.$metadataService);
                            this.target[propertyToReload.Name] = new Proxy(wrapper, new LazyLoadedProxyHandler(this.$metadataService));
                        }
                    }
                }
                return x;
            });
    }

    handleCheckPoint(identifier: string, previousIdentifier: string, action: string): void
    {
        if (action === CheckpointAction.Commit && this.checkpoint[identifier])
        {
            this.checkpoint[previousIdentifier] = this.checkpoint[identifier];
            delete this.checkpoint[identifier];
        }
        else if (action === CheckpointAction.Rollback && this.checkpoint[identifier])
            Object.keys(this.checkpoint[identifier])
                .filter(key => !Utilities.isPrototypePollutingKey(key))
                .forEach(key => this.target[key] = this.checkpoint[identifier][key]);
        delete this.checkpoint[identifier];
    }
}

export const enum CheckpointAction
{
    Commit = "Commit",
    Rollback = "Rollback"
}
